import { currentShadowId, getMetaContent } from './meta';
import { logError } from './logger';

/**
 * OneSignal Web SDK v16 loader + subscription state store.
 *
 * Browser push is a per-browser enrollment: opting in subscribes THIS browser
 * for the signed-in user (via OneSignal.login with users.external_id_onesignal),
 * and the server's existing external_id-alias targeting fans pushes out to it.
 * Per-workspace notification settings still decide when a user is pushed.
 *
 * The CDN script only loads when this browser was previously enrolled by the
 * current user (localStorage marker, see bootOneSignal) or the user flips the
 * enrollment toggle — sessions that never touch push never pay for the SDK.
 */

const CDN_SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
const ENROLLED_MARKER_KEY = 'onesignal:enrolled_external_id';
// A scope of its own keeps the push worker clear of the root asset-caching
// worker (/sw.js), whose dev and deploy-time unregister paths target root
// scope only — unregistering the push worker would kill the subscription.
const SERVICE_WORKER_SCOPE = '/push/onesignal/';
const LOGOUT_TIMEOUT_MS = 1500;

const appId = () => getMetaContent('onesignal:app_id');
const externalId = () => getMetaContent('current_user:onesignal_external_id');

const readEnrolledMarker = () => {
  try {
    return window.localStorage.getItem(ENROLLED_MARKER_KEY);
  } catch {
    return null;
  }
};

const writeEnrolledMarker = (value) => {
  try {
    if (value == null) window.localStorage.removeItem(ENROLLED_MARKER_KEY);
    else window.localStorage.setItem(ENROLLED_MARKER_KEY, value);
  } catch {
    // private mode etc. — enrollment still works, it just won't auto-boot next session
  }
};

let supportedMemo = null;

/**
 * Whether this page can enroll for browser push at all. False in the native
 * shells (Electron already registers APNs via /v1/onesignal; iOS/Android use
 * native SDKs), while staff-shadowing (never enroll a staff browser as the
 * shadowed user), and when the environment lacks push support or credentials.
 * None of the inputs change over a page's lifetime, so the result is memoized.
 */
export const browserPushSupported = () => {
  supportedMemo ??=
    typeof window !== 'undefined' &&
    getMetaContent('app:variant') === 'web' &&
    !currentShadowId() &&
    Boolean(appId() && externalId()) &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    window.isSecureContext;

  return supportedMemo;
};

export class BrowserPushPermissionDenied extends Error {}

// optedIn is seeded from the enrollment marker so the settings toggle renders
// without loading the SDK; syncFromSdk corrects it once the SDK actually runs.
let state = {
  permission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
  optedIn: typeof window !== 'undefined' && Boolean(externalId()) && readEnrolledMarker() === externalId(),
};

const listeners = new Set();

const setState = (patch) => {
  if (Object.keys(patch).every((key) => patch[key] === state[key])) return;

  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const syncFromSdk = (OneSignal) => {
  setState({
    permission: Notification.permission,
    optedIn: Boolean(OneSignal.User.PushSubscription.optedIn),
  });
};

/** Snapshot for useSyncExternalStore — referentially stable between changes. */
export const getOneSignalState = () => state;

export const subscribeOneSignalState = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Three memoized stages, each resetting itself on failure so any transient
// error is retryable from the next interaction — no page reload required.
let sdkPromise = null; // CDN script loaded; resolves with the OneSignal namespace
let initPromise = null; // SDK initialized against our scoped worker (no identity)
let readyPromise = null; // initialized AND logged in as the current user

const loadSdk = () => {
  sdkPromise ??= new Promise((resolve, reject) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    // A callback left queued by a failed load is harmless: it can only resolve
    // an already-settled promise. init lives in ensureSdkInitialized (memoized
    // separately), so a retry can never double-init.
    window.OneSignalDeferred.push((OneSignal) => resolve(OneSignal));

    const script = document.createElement('script');
    script.src = CDN_SDK_URL;
    script.defer = true;
    script.onerror = () => {
      script.remove();
      sdkPromise = null;
      reject(new Error('OneSignal SDK failed to load'));
    };
    document.head.appendChild(script);
  });

  return sdkPromise;
};

// Init without an identity. The logout path needs the SDK (with its persisted
// local identity) but must never link the current account first.
const ensureSdkInitialized = () => {
  initPromise ??= loadSdk()
    .then(async (OneSignal) => {
      await OneSignal.init({
        appId: appId(),
        serviceWorkerParam: { scope: SERVICE_WORKER_SCOPE },
        serviceWorkerPath: 'push/OneSignalSDKWorker.js',
        welcomeNotification: { disable: true },
      });

      OneSignal.Notifications.addEventListener('permissionChange', () => syncFromSdk(OneSignal));
      OneSignal.User.PushSubscription.addEventListener('change', () => syncFromSdk(OneSignal));
      syncFromSdk(OneSignal);

      return OneSignal;
    })
    .catch((error) => {
      initPromise = null;
      throw error;
    });

  return initPromise;
};

/**
 * Idempotently load the CDN SDK, init it against our scoped service worker,
 * and log in the current user's external id. Resolves with the OneSignal
 * namespace once subscription state is wired into the store.
 */
export const ensureOneSignalReady = () => {
  if (!browserPushSupported()) return Promise.reject(new Error('browser push is not supported here'));

  readyPromise ??= ensureSdkInitialized()
    .then(async (OneSignal) => {
      // Idempotent per OneSignal docs; safe to call every boot.
      await OneSignal.login(externalId());
      syncFromSdk(OneSignal);
      return OneSignal;
    })
    .catch((error) => {
      readyPromise = null;
      throw error;
    });

  return readyPromise;
};

/** Enroll this browser: prompts for permission (when undecided) and opts in. */
export const optInBrowserPush = async () => {
  const OneSignal = await ensureOneSignalReady();
  await OneSignal.User.PushSubscription.optIn();
  syncFromSdk(OneSignal);

  if (Notification.permission !== 'granted') {
    throw new BrowserPushPermissionDenied('notification permission was not granted');
  }

  writeEnrolledMarker(externalId());
};

/** Opt this browser out (keeps browser permission; OneSignal stops sending). */
export const optOutBrowserPush = async () => {
  const OneSignal = await ensureOneSignalReady();
  await OneSignal.User.PushSubscription.optOut();

  writeEnrolledMarker(null);
  syncFromSdk(OneSignal);
};

/**
 * Best-effort unlink of this browser's subscription from the user, for logout.
 * Timeout-raced so a slow CDN or API can never hang the logout form.
 */
export const logoutOneSignal = async () => {
  if (!browserPushSupported()) return;
  // Only worth a cold SDK init when this session's user owns the enrollment —
  // a stale marker from another account must not delay the logout navigation.
  if (!readyPromise && readEnrolledMarker() !== externalId()) return;

  try {
    // Await an in-flight login (readyPromise) so a late login can't land after
    // logout() and relink the outgoing account; otherwise init-only (never
    // login) — the SDK's persisted local identity is enough for logout() to
    // unlink, and a cold logout must not link the outgoing account first.
    await Promise.race([
      (readyPromise || ensureSdkInitialized()).then((OneSignal) => OneSignal.logout()),
      new Promise((_resolve, reject) => {
        setTimeout(() => reject(new Error('onesignal logout timed out')), LOGOUT_TIMEOUT_MS);
      }),
    ]);
  } catch (error) {
    logError('onesignal', 'Best-effort OneSignal logout failed', { error });
  }
};

/**
 * SPA boot hook: silently resume the SDK only when this browser was enrolled
 * by the currently signed-in user. A different account must flip the toggle
 * themselves — never inherit another user's consent.
 */
export const bootOneSignal = () => {
  if (!browserPushSupported()) return;
  if (readEnrolledMarker() !== externalId()) return;

  ensureOneSignalReady().catch((error) => {
    logError('onesignal', 'OneSignal boot failed', { error });
  });
};
